/**
 * Starea unui transfer, pe ecran.
 *
 * Afișarea era binară — `trimis`, sau altfel „Eșuat" cu roșu. De când rândul se scrie ÎNAINTE de
 * apelul către ExpertAccounts, ca să rezerve referința, mai există o stare: `nou`. Ea nu înseamnă
 * „a eșuat", ci „nu se știe încă" — iar diferența contează cel mai mult exact înaintea unei
 * ștergeri, unde vechiul mesaj promitea că n-a plecat niciun document.
 */

export interface Transfer {
  id: number
  referinta: string
  campanieId: number | null
  cerereId: number | null
  locSursa: number
  locDestinatie: number
  /* Numele gestiunilor, ca textul să spună „Mostre → Dorian Ion", nu „7 → 35". */
  locSursaNume?: string | null
  locDestinatieNume?: string | null
  /** Destinația a fost corectată în CRM, după o îndreptare făcută de mână în EA. */
  destinatieVeche?: number | null
  destinatieCorectataDeNume?: string | null
  status: string
  eaDocNo: string | null
  raspunsEa: string | null
  observatii: string | null
  createdAt: string
  doctype: string
  linkPrimireEa: string | null
  tcInStatus: string | null
  tcInDocNo: string | null
  tcInRaspuns: string | null
  /** Anulat prin storno: marfa s-a întors cu un transfer în sens invers. */
  anulatLa: string | null
  anulatDeNume: string | null
  stornoRefno: string | null
  linii: { sku: string; denumire: string; cantitate: number }[]
}

export function EticheaStareTransfer({ transfer }: { transfer: Transfer }) {
  /*
    ANULAT bate orice altă stare: rândul a plecat, dar marfa s-a întors. Fără eticheta asta
    înaintea celorlalte, un transfer anulat ar arăta mai departe „Trimis" și ar părea că marfa e
    tot acolo unde n-ar trebui să fie.
  */
  if (transfer.status === 'anulat') {
    return (
      <span
        className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted line-through"
        title={
          transfer.stornoRefno
            ? `Marfa s-a întors prin transferul ${transfer.stornoRefno}.`
            : 'Marcat anulat. Documentul din ExpertAccounts a fost făcut acolo, corecția tot acolo se face.'
        }
      >
        Anulat{transfer.eaDocNo ? ` · ${transfer.eaDocNo}` : ''}
      </span>
    )
  }
  if (transfer.status === 'trimis') {
    return (
      <span className="rounded-full bg-b4l-green-light px-2 py-0.5 text-xs text-b4l-green-dark">
        Trimis{transfer.eaDocNo ? ` · ${transfer.eaDocNo}` : ''}
      </span>
    )
  }
  /*
    Înregistrat manual: mutarea s-a făcut DIRECT în ExpertAccounts, iar rândul doar o notează.
    Eticheta trebuie să spună asta limpede — la socoteala mărfii contează la fel ca un transfer
    trimis, dar documentul nu e al nostru și primirea `tc-in` nu se poate face de aici.
  */
  if (transfer.status === 'inregistrat') {
    return (
      <span
        className="rounded-full bg-b4l-blue-light px-2 py-0.5 text-xs text-b4l-blue-dark"
        title="Mutarea s-a făcut în ExpertAccounts; aici e doar notată. Din CRM n-a plecat niciun document."
      >
        Înregistrat{transfer.eaDocNo ? ` · ${transfer.eaDocNo}` : ''}
      </span>
    )
  }
  if (transfer.status === 'nou') {
    return (
      <span
        className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark"
        title="Referința e rezervată, dar nu știm dacă documentul a ajuns în ExpertAccounts. Verifică acolo."
      >
        În curs — nedecis
      </span>
    )
  }
  return (
    <span className="rounded-full bg-b4l-red-light px-2 py-0.5 text-xs text-b4l-red-dark">
      Eșuat
    </span>
  )
}

/** Ce scrie în confirmarea de ștergere. La `nou` nu putem promite că n-a plecat nimic. */
export function avertismentStergere(t: Transfer): string {
  if (t.status === 'inregistrat') {
    return `${t.referinta} doar înregistrează mutarea făcută în ExpertAccounts (documentul ${t.eaDocNo ?? '—'}).

Ștergerea de aici nu modifică nimic în ExpertAccounts — dispare doar evidența din CRM, iar raportul pe agent nu va mai arăta această mutare.

Continui?`
  }
  if (t.status === 'trimis') {
    return `${t.referinta} a produs documentul ${t.eaDocNo ?? '(fără număr)'} în ExpertAccounts.\n\nȘtergerea de aici NU îl șterge și acolo. Fă-o întâi în ExpertAccounts, altfel rămâne acolo un document fără urmă în CRM.\n\nContinui?`
  }
  if (t.status === 'nou') {
    return `${t.referinta} a rămas „în curs": s-a rezervat numărul, dar nu știm dacă documentul a ajuns în ExpertAccounts.\n\nVerifică ACOLO înainte să ștergi. Dacă documentul există, șterge-l întâi în EA.\n\nContinui?`
  }
  return `Ștergi ${t.referinta}? N-a produs niciun document în EA.`
}

/**
 * Ce scrie în confirmarea de ANULARE.
 *
 * Textul arată mișcarea inversă, gestiune cu gestiune și produs cu produs: omul apasă pe un buton
 * care emite un document de gestiune în ExpertAccounts, deci trebuie să vadă exact ce document.
 *
 * Alexandru, 30.09.2026: transferul pentru „Her Moment" plecase în gestiunea Sampling în loc de a
 * lui Dorian. Anularea nu e ștergere — marfa chiar s-a mutat, și se întoarce cu document.
 */
export function avertismentAnulare(t: Transfer): string {
  const loc = (id: number, nume?: string | null) => (nume ? `${nume} (${id})` : String(id))
  if (t.status === 'inregistrat') {
    return [
      `${t.referinta} doar înregistrează o mutare făcută în ExpertAccounts.`,
      '',
      'Aici se marchează anulat, dar documentul din ExpertAccounts trebuie corectat acolo — CRM-ul nu l-a emis.',
      '',
      'Continui?',
    ].join('\n')
  }
  return [
    `Se emite în ExpertAccounts transferul INVERS al lui ${t.referinta}${
      t.eaDocNo ? ` (${t.eaDocNo})` : ''
    }:`,
    '',
    `${loc(t.locDestinatie, t.locDestinatieNume)} → ${loc(t.locSursa, t.locSursaNume)}`,
    ...t.linii.map((l) => `${l.denumire} ×${l.cantitate}`),
    '',
    'Marfa se întoarce, iar cererea se redeschide. Continui?',
  ].join('\n')
}

/**
 * Confirmarea ȘTERGERII, ca verificare, nu ca avertisment.
 *
 * Alexandru, 30.09.2026: „adaugă doar o confirmare de verificare, că transferul trebuie șters în
 * Expert". Textul spunea deja asta, dar un `confirm` se apasă din reflex — iar consecința nu se
 * vede: documentul rămâne în ERP, mutând marfa, fără nicio urmă în CRM. Divergența se descoperă
 * la inventar.
 *
 * De aceea se cere NUMĂRUL documentului, tastat. Nu e o parolă: e singurul mod de a te obliga să
 * te uiți în ExpertAccounts înainte, fiindcă de acolo îl iei. Cine îl are în față l-a și căutat.
 *
 * Un transfer fără document nu pune întrebarea — n-are ce fi șters acolo.
 */
export function confirmaStergere(t: Transfer): boolean {
  if (!t.eaDocNo) return window.confirm(avertismentStergere(t))

  const raspuns = window.prompt(
    [
      `${t.referinta} a produs documentul ${t.eaDocNo} în ExpertAccounts.`,
      '',
      'Ștergerea de aici NU îl șterge și acolo. Șterge-l întâi în ExpertAccounts — altfel rămâne',
      'acolo un document care mută marfa, fără nicio urmă în CRM.',
      '',
      `Ca să confirmi că l-ai șters în EA, scrie numărul documentului: ${t.eaDocNo}`,
    ].join('\n'),
    '',
  )
  if (raspuns === null) return false
  if (raspuns.trim().toLowerCase() !== t.eaDocNo.trim().toLowerCase()) {
    window.alert(`Nu se potrivește cu ${t.eaDocNo}. Ștergerea nu s-a făcut.`)
    return false
  }
  return true
}
